import React, { useState, useMemo } from 'react';
import { get } from 'lodash-es';
import { Inspector } from '@quintype/em/components/inspector';
import { Dialog } from '@quintype/em/components/dialog';
import validate from 'validate.js';
import { createOrUpdateStoryAttributeSettings } from '../../api';
import { CreatableSelect } from '@quintype/em/components/select';
import { TextField } from '@quintype/em/components/text-field';
import { ErrorMessage } from '@quintype/em/components/error-message';
import { countries } from 'countries-list';

import './country-groups-inspector.scss';

const generateCountryFilter = (selectedCountryCodeList) => {
  return Object.keys(countries)
    .map(code => ({ label: countries[code].name, value: code }))
    .filter(country => !selectedCountryCodeList.includes(country.value));
};

const generateSelectedCountries = (selectedCountryCodeList) => {
  return selectedCountryCodeList.map(code => ({
    label: countries[code].name,
    value: code
  }));
};

const CountryGroupsInspector = (props) => {
  const [countryGroup, setCountryGroup] = useState(props.countryGroup);
  const [errors, setErrors] = useState([]);
  const [isActionButtonDisabled, setActionButtonDisabled] = useState(props.countryGroup.enabled || !props.countryGroup.id);
  const [isSecondaryButtonDisabled, setSecondaryButtonDisabled] = useState(!props.countryGroup.enabled);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('save');
  const [selectedCountries, setSelectedCountries] = useState(
    generateSelectedCountries(get(props.countryGroup.details, 'countries') || [])
  );
  const [countriesFilter, setCountriesFilter] = useState(
    generateCountryFilter(get(props.countryGroup.details, 'countries') || [])
  );

  const constraints = useMemo(() => ({
    name: {
      presence: true,
      length: {
        maximum: 100,
        minimum: 1,
        tooShort: "can't be blank",
        tooLong: 'is too long (maximum is %{count} characters)'
      }
    },
    countries: {
      presence: true,
      type: 'array',
      length: {
        minimum: 1,
        message: 'must contain at least one country'
      }
    }
  }), []);

  const dialogContent = (type) => {
    if (type === 'disable') {
      return {
        textContent: 'Do you want to disable this country group?',
        buttonLabel: 'Disable',
        actionButtonCallBack: () => updateCountryGroupAndPost(false)
      };
    }
    return {
      textContent: 'Do you want to save changes to this country group?',
      buttonLabel: 'Save',
      actionButtonCallBack: () => updateCountryGroupAndPost(true)
    };
  };

  const updateCountryGroupField = (updatedFields, callback) => {
    const updated = { ...countryGroup, ...updatedFields };
    setCountryGroup(updated);
    setActionButtonDisabled(false);
    setSecondaryButtonDisabled(false);
    if (callback) callback(updated);
  };

  const updateSelectedCountries = (newSelected) => {
    const codes = newSelected.map(item => item.value);
    setSelectedCountries(newSelected);
    setCountriesFilter(generateCountryFilter(codes));
    updateCountryGroupField({ details: { countries: codes } });
  };

  const updateCountryGroupAndPost = (enabled) => {
    updateCountryGroupField({ enabled: enabled }, postCountryGroups);
  };

  const postCountryGroups = (countryGroupToPost) => {
    createOrUpdateStoryAttributeSettings(countryGroupToPost).then(
      () => {
        setErrors([]);
        setActionButtonDisabled(true);
        props.actionInspector();
      },
      (e) => {
        e['save_error'] = e['errors'][0];
        setErrors(e);
        setDialogOpen(false);
      }
    );
  };

  const saveCountryGroup = (action) => {
    if (action === 'disable') {
      setCountryGroup(prev => ({ ...prev, enabled: false }));
    }

    setErrors([]);
    const countryGroupToValidate = {
      name: countryGroup.name,
      countries: get(countryGroup.details, 'countries')
    };

    const validationError = validate(countryGroupToValidate, constraints);

    if (validationError) {
      setErrors(validationError);
    } else if (props.usedCountryGroupNames.includes(countryGroup.name.toLowerCase())) {
      setErrors({ name: ['Name should be unique'] });
    } else {
      setDialogOpen(true);
      setDialogMode(action);
    }
  };

  const dialog = dialogContent(dialogMode);

  return (
    <div className="country-groups-edit-wrapper">
      <Inspector
        title="Country Group"
        isActive={props.isInspectorActive}
        onClose={props.onClose}
        actionButtonLabel="Save"
        onActionButtonClick={() => saveCountryGroup('save')}
        secondaryButtonLabel="Disable"
        onSecondaryButtonClick={() => saveCountryGroup('disable')}
        isSecondaryButtonDisabled={isSecondaryButtonDisabled}
        isActionButtonDisabled={isActionButtonDisabled}
      >
        <div className="error-message-container">
          <ErrorMessage message={errors['save_error']} />
        </div>
        <TextField
          label="Group Name"
          placeholder="Type Country Group Name"
          value={countryGroup.name || ''}
          onChange={(e) => updateCountryGroupField({ name: e })}
          errorMessage={get(errors, ['name', 0])}
        />
        <div className="country-select">
          <CreatableSelect
            label="Countries"
            options={countriesFilter}
            value={selectedCountries}
            onChange={updateSelectedCountries}
            isMulti
            isClearable={false}
            menuPlacement="bottom"
            closeMenuOnSelect={false}
            isValidNewOption={() => false}
            errorMessage={get(errors, ['countries', 0])}
          />
        </div>
      </Inspector>

      <Dialog
        isOpen={isDialogOpen}
        actionButtonLabel={dialog.buttonLabel}
        onAction={dialog.actionButtonCallBack}
        onClose={() => setDialogOpen(false)}
      >
        <p className="dialog-text">{dialog.textContent}</p>
      </Dialog>
    </div>
  );
};

export { CountryGroupsInspector };
